import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, Outlet } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { SocketProvider } from './context/SocketContext';
import { ROLE_NAV, RISK_DEPT_ROLES, homeFor } from './components/Navigation';
import Layout from './components/Layout';
import Login from './pages/Login';
import Dashboard from './pages/Dashboard';
import MDDashboard from './pages/MDDashboard';
import Merchandising from './pages/Merchandising';
import Procurement from './pages/Procurement';
import StoreInward from './pages/StoreInward';
import Runner from './pages/Runner';
import GateRegister from './pages/GateRegister';
import Activity from './pages/Activity';
import Huddle from './pages/Huddle';
import HelpSlip from './pages/HelpSlip';
import Production from './pages/Production';
import PPC from './pages/PPC';
import Profitability from './pages/Profitability';
import Performance from './pages/Performance';
import Tasks from './pages/Tasks';
import JobWork from './pages/JobWork';
import Finance from './pages/Finance';
import Requests from './pages/Requests';
import MerchFabricCalc from './pages/MerchFabricCalc';
import TNA from './pages/TNA';
import SalesPlanView from './pages/SalesPlanView';
import VendorRegister from './pages/VendorRegister';
import Apply from './pages/Apply';
import CandidateTest from './pages/CandidateTest';
import VendorReview from './pages/VendorReview';
import CentreBoard from './pages/CentreBoard';
import ProductionBulletin from './pages/ProductionBulletin';
import IE from './pages/IE';
import SamplingTMS from './pages/SamplingTMS';
import OrderExecution from './pages/OrderExecution';
import RiskSignOffs from './pages/RiskSignOffs';
import RiskItems from './pages/RiskItems';
import RiskAnalysisPage from './pages/RiskAnalysisPage';
import FullKittingPage from './pages/FullKittingPage';
import PPMPage from './pages/PPMPage';
import Maintenance from './pages/Maintenance';
import Masters from './pages/Masters';
import PendingFabrics from './pages/PendingFabrics';
import GstTagging from './pages/GstTagging';
import DepartmentsMaster from './pages/hr/DepartmentsMaster';
import BuyerMaster from './pages/BuyerMaster';
import CostEstimation from './pages/CostEstimation';
import AdminUsers from './pages/AdminUsers';
import ChangePassword from './pages/ChangePassword';
import Quality from './pages/Quality';
import HR from './pages/HR';
import FieldAttendance from './pages/FieldAttendance';
import Assessment from './pages/Assessment';
import Training from './pages/Training';
import ErrorBoundary from './components/ErrorBoundary';
import { NotFound, Forbidden } from './pages/ErrorPages';

// Department-scoped roles (ROLE_NAV) may only open paths in their allow-list;
// any other route sends them back to the dashboard. Roles not in ROLE_NAV are
// unrestricted. Dashboard is always reachable.
const pathAllowedForRole = (role, pathname) => {
  const allow = ROLE_NAV[role];
  if (!allow) return true;
  // Auditor is HR-only, Store is kitting-only, Procurement is procurement-only — none
  // of these scoped roles get the cross-module dashboard.
  if (pathname === '/dashboard') return role !== 'auditor' && role !== 'store' && role !== 'procurement_officer';
  // Risk Sign-offs is universal — any user can own a flagged risk and must be
  // able to reach their sign-off queue regardless of their department allow-list.
  if (pathname === '/risk-sign-offs') return true;
  if (pathname === '/risk-items') return true;
  if (pathname === '/risk-analysis') return true;
  // Training Assessment is universal — every role takes their own test.
  if (pathname === '/assessment') return true;
  // Training Videos is universal too — every role may watch the company's training videos (mig 291).
  if (pathname === '/training') return true;
  // Huddle is universal EXCEPT the gate: everyone attends the 10:05 stand-up and fills in
  // their own sheet, whatever their department allow-list says. Gate Security is a
  // data-entry operator keying a paper register and does not attend (ruling 9), so it is
  // the one role kept out — matching the roster the server builds.
  if (pathname === '/huddle') return role !== 'gate';
  // Help Slip is universal with NO exclusions — every one of the 17 active users may raise
  // one, including gate. Reading someone else's is refused server-side, not here.
  if (pathname === '/help-slip') return true;
  return allow.some(p => pathname === p || pathname.startsWith(p + '/'));
};

// Auth + sidebar layout for every private page. Outlet renders the matched route.
const ProtectedLayout = () => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>
  );
  if (!user) return <Navigate to="/login" replace />;
  // Forced password change (admin flag): block all app access until done.
  if (user.must_change_password) return <Navigate to="/change-password" replace />;
  if (!pathAllowedForRole(user.role, pathname)) return <Navigate to={homeFor(user.role)} replace />;
  return <Layout><Outlet /></Layout>;
};

// Restricts a route to the given roles; everyone else is sent to the dashboard.
const RoleRoute = ({ roles, children }) => {
  const { user, loading } = useAuth();
  const { pathname } = useLocation();
  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>
  );
  if (!user) return <Navigate to="/login" />;
  if (!roles.includes(user.role)) return <Navigate to={homeFor(user.role)} />;
  if (!pathAllowedForRole(user.role, pathname)) return <Navigate to={homeFor(user.role)} replace />;
  return children;
};

const AdminRoute = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>
  );
  if (!user) return <Navigate to="/login" />;
  return user.role === 'admin' ? children : <Forbidden />;
};

// Requires a logged-in user but NO role/path/force-change gating — used for the
// change-password screen so it's reachable even while the force-change flag is set.
const RequireUser = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Loading...</div>
  );
  if (!user) return <Navigate to="/login" replace />;
  return children;
};

// Role-aware home: where "/" sends a logged-in user (auditor → HR, else dashboard).
const Home = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return <Navigate to={user ? homeFor(user.role) : '/login'} replace />;
};

const AppRoutes = () => (
  <SocketProvider>
    <Routes>
      {/* Public routes (no sidebar layout) */}
      <Route path="/login" element={<Login />} />
      <Route path="/vendor-register/:token" element={<VendorRegister />} />
      <Route path="/vendor-register" element={<VendorRegister />} />
      {/* Public candidate intake — /apply/rsr or /apply/deroi */}
      <Route path="/apply/:company" element={<Apply />} />
      {/* Public candidate assessment — tokened link HR sends (no login) */}
      <Route path="/candidate-test/:token" element={<CandidateTest />} />
      {/* Change password — logged-in but outside the layout/force-change gate */}
      <Route path="/change-password" element={<RequireUser><ChangePassword /></RequireUser>} />

      {/* Private routes — wrapped in the sidebar layout */}
      <Route element={<ProtectedLayout />}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/md-dashboard" element={<RoleRoute roles={['admin', 'management']}><MDDashboard /></RoleRoute>} />
        <Route path="/merchandising" element={<Merchandising />} />
        <Route path="/procurement" element={<Procurement />} />
        {/* Activity log — every authenticated role may open it; the SERVER decides whether
            they see everyone or only themselves. No role gate here on purpose. */}
        <Route path="/activity" element={<Activity />} />
        {/* Huddle — everyone attends and fills in their own sheet, so no role gate here.
            The SERVER decides whether they see the whole team or only themselves, and the
            gate role is excluded from the roster (ruling 9) via pathAllowedForRole below. */}
        <Route path="/huddle" element={<Huddle />} />
        {/* Help Slip — EVERY role may raise one (ruling 6), gate included; unlike the
            huddle, which excludes gate. The SERVER limits who can READ them: the person
            who raised it, and admin. */}
        <Route path="/help-slip" element={<HelpSlip />} />
        <Route path="/store-inward" element={<RoleRoute roles={['admin', 'management', 'stores_incharge', 'store', 'gate']}><StoreInward /></RoleRoute>} />
        {/* Everyone submits errands — a gate on raising would lose exactly the late
            request the board exists to catch. Curating the area list is gated, in the API. */}
        <Route path="/runner" element={<Runner />} />
        {/* Byte-identical role list to REG_VIEW in gateRegister.routes.js — the screen and
            the endpoint must not disagree about who may read the register. */}
        <Route path="/gate-register" element={<RoleRoute roles={['admin', 'management', 'stores_incharge', 'store', 'gate', 'deroi_operator']}><GateRegister /></RoleRoute>} />
        <Route path="/deroi" element={<RoleRoute roles={['admin', 'management', 'deroi_operator']}><Procurement deroiMode /></RoleRoute>} />
        <Route path="/production" element={<Production />} />
        <Route path="/quality" element={<Quality />} />
        <Route path="/ppc" element={<PPC />} />
        <Route path="/profitability" element={<RoleRoute roles={['admin', 'management']}><Profitability /></RoleRoute>} />
        <Route path="/performance" element={<Performance />} />
        <Route path="/tasks" element={<Tasks />} />
        <Route path="/job-work" element={<JobWork />} />
        {/* hr is here ONLY to verify Day Book entries (mig 176). This grants the ROUTE and
            nothing else: Finance.js clamps hr to the single Day Book tab and hides the KPI
            strip, and the finance endpoints keep their own role gates (FIN excludes hr), so
            hr reaches the register and no other finance surface. ROLE_NAV.hr and the sidebar
            already listed /finance; without hr here the guard sent it to /dashboard, which is
            why HR Finance access never worked despite 9428b79. */}
        <Route path="/finance" element={<RoleRoute roles={['admin', 'management', 'stores_incharge', 'accounts', 'hr', 'deroi_operator']}><Finance /></RoleRoute>} />
        <Route path="/requests" element={<Requests />} />
        <Route path="/fabric-calculator" element={<RoleRoute roles={['admin', 'management', 'procurement_officer', 'merchandiser']}><MerchFabricCalc /></RoleRoute>} />
        <Route path="/pending-fabrics" element={<RoleRoute roles={['admin', 'management', 'merchandiser']}><PendingFabrics /></RoleRoute>} />
        <Route path="/tna" element={<TNA />} />
        <Route path="/sales-plan" element={<RoleRoute roles={['production_incharge', 'production_supervisor', 'ppc_manager']}><SalesPlanView /></RoleRoute>} />
        <Route path="/vendor-review" element={<VendorReview />} />
        <Route path="/centre-board" element={<CentreBoard />} />
        <Route path="/production-bulletin" element={<ProductionBulletin />} />
        <Route path="/ie" element={<IE />} />
        <Route path="/sampling-tms" element={<RoleRoute roles={['admin', 'management', 'merchandiser', 'ppc_manager', 'production_incharge', 'production_supervisor']}><SamplingTMS /></RoleRoute>} />
        <Route path="/order-execution" element={<OrderExecution />} />
        <Route path="/risk-sign-offs" element={<RiskSignOffs />} />
        <Route path="/risk-items" element={<RiskItems />} />
        <Route path="/risk-analysis" element={<RoleRoute roles={[...RISK_DEPT_ROLES, 'admin', 'management']}><RiskAnalysisPage /></RoleRoute>} />
        <Route path="/full-kitting" element={<FullKittingPage />} />
        <Route path="/ppm" element={<PPMPage />} />
        {/* maintenance_incharge added here TOO. This is the gate that actually admits the route —
            ROLE_NAV and the sidebar only decide what is SHOWN. 9428b79 updated the other two for
            hr/finance and missed this one, and the feature never worked as a result. */}
        <Route path="/maintenance" element={<RoleRoute roles={['admin', 'management', 'production_incharge', 'production_supervisor', 'ppc_manager', 'maintenance_incharge']}><Maintenance /></RoleRoute>} />
        <Route path="/masters" element={<Masters />} />
        <Route path="/gst-tagging" element={<RoleRoute roles={['admin', 'accounts', 'management']}><GstTagging /></RoleRoute>} />
        <Route path="/departments" element={<RoleRoute roles={['admin', 'management', 'hr']}><DepartmentsMaster /></RoleRoute>} />
        <Route path="/buyer-master" element={<RoleRoute roles={['admin', 'management']}><BuyerMaster /></RoleRoute>} />
        <Route path="/cost-estimation" element={<CostEstimation />} />
        <Route path="/admin/users" element={<AdminRoute><AdminUsers /></AdminRoute>} />
        <Route path="/hr" element={<RoleRoute roles={['admin', 'management', 'hr', 'auditor']}><HR /></RoleRoute>} />
        <Route path="/my-hr" element={<HR selfService />} />
        <Route path="/field-attendance" element={<FieldAttendance />} />
        <Route path="/assessment" element={<Assessment />} />
        <Route path="/training" element={<Training />} />
      </Route>

      <Route path="/" element={<Home />} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  </SocketProvider>
);

export default function App() {
  return (
    <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <AppRoutes />
        </BrowserRouter>
      </AuthProvider>
    </ErrorBoundary>
  );
}